import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { BRANCHES, GRADE_LEVELS } from '@/mocks/mockData';

interface OgrenciRow {
  id: string;
  name: string;
  parentName: string;
  phone: string;
  city: string;
  grade: string;
  branches: string[];
  lessonType: string;
  assignedTeacherId: string | null;
  assignedTeacherName: string;
  status: 'Aktif' | 'Pasif' | 'Takipte';
  totalLessons: number;
  totalPayment: number;
  lastLessonDate: string | null;
  notes: string;
  dersUcreti: number | null;
}

interface EditForm {
  name: string; parentName: string; phone: string; city: string;
  grade: string; branches: string[]; lessonType: string;
  assignedTeacherId: string; status: string; notes: string; dersUcreti: string;
}

const emptyAddForm = {
  name: '', parentName: '', phone: '', city: 'Antalya',
  grade: '', branches: [] as string[], lessonType: 'Birebir Online',
  assignedTeacherId: '', status: 'Aktif', notes: '', dersUcreti: '',
};

function dbToLessonType(db: string | null): string {
  switch (db) {
    case 'bireysel_online': return 'Birebir Online';
    case 'yuz_yuze': return 'Yüz Yüze';
    case 'grup': return 'Grup Dersi';
    case 'kpss': return 'KPSS';
    default: return db || 'Birebir Online';
  }
}

function lessonTypeToDb(label: string): string | null {
  switch (label) {
    case 'Birebir Online': return 'bireysel_online';
    case 'Yüz Yüze': return 'yuz_yuze';
    case 'Grup Dersi': return 'grup';
    case 'KPSS': return 'kpss';
    default: return null;
  }
}

const statusColors: Record<string, string> = {
  Aktif: 'bg-green-100 text-green-700',
  Pasif: 'bg-red-100 text-red-700',
  Takipte: 'bg-yellow-100 text-yellow-700',
};

export default function OgrencilerPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<OgrenciRow[]>([]);
  const [teachers, setTeachers] = useState<{ id: string; ad_soyad: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [editItem, setEditItem] = useState<OgrenciRow | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [deleteItem, setDeleteItem] = useState<OgrenciRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState(emptyAddForm);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [ogrencilerRes, ogretmenlerRes] = await Promise.all([
      supabase.from('egtpanel_ogrenciler').select('*').order('olusturma_tarihi', { ascending: false }),
      supabase.from('egtpanel_ogretmenler').select('id, ad_soyad').order('ad_soyad'),
    ]);

    const ogretmenMap: Record<string, string> = {};
    (ogretmenlerRes.data ?? []).forEach((t: { id: string; ad_soyad: string }) => { ogretmenMap[t.id] = t.ad_soyad; });
    setTeachers(ogretmenlerRes.data ?? []);

    if (ogrencilerRes.data) {
      setData(ogrencilerRes.data.map((r) => ({
        id: r.id,
        name: r.ogrenci_adi,
        parentName: r.veli_adi ?? '',
        phone: r.telefon ?? '',
        city: r.sehir ?? 'Antalya',
        grade: r.sinif ?? '',
        branches: Array.isArray(r.branslar) ? r.branslar : [],
        lessonType: dbToLessonType(r.ders_tipi),
        assignedTeacherId: r.atanan_ogretmen_id ?? null,
        assignedTeacherName: r.atanan_ogretmen_id ? (ogretmenMap[r.atanan_ogretmen_id] ?? '-') : '-',
        status: r.aktif ? 'Aktif' : 'Pasif',
        totalLessons: r.toplam_ders_sayisi ?? 0,
        totalPayment: Number(r.toplam_odeme ?? 0),
        lastLessonDate: r.son_ders_tarihi ?? null,
        notes: r.notlar ?? '',
        dersUcreti: r.ders_ucreti != null ? Number(r.ders_ucreti) : null,
      })));
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = data.filter((s) => {
    const q = search.toLowerCase();
    const matchQ = !q || s.name.toLowerCase().includes(q) || s.parentName.toLowerCase().includes(q) || s.phone.includes(q);
    const matchStatus = !filterStatus || s.status === filterStatus;
    return matchQ && matchStatus;
  });

  const openEdit = (s: OgrenciRow) => {
    setEditItem(s);
    setEditForm({
      name: s.name, parentName: s.parentName, phone: s.phone,
      city: s.city, grade: s.grade, branches: s.branches,
      lessonType: s.lessonType, assignedTeacherId: s.assignedTeacherId ?? '',
      status: s.status, notes: s.notes,
      dersUcreti: s.dersUcreti != null ? String(s.dersUcreti) : '',
    });
  };

  const toggleBranch = (b: string) => setEditForm((p) => p ? ({ ...p, branches: p.branches.includes(b) ? p.branches.filter((x) => x !== b) : [...p.branches, b] }) : p);
  const toggleAddBranch = (b: string) => setAddForm((p) => ({ ...p, branches: p.branches.includes(b) ? p.branches.filter((x) => x !== b) : [...p.branches, b] }));

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const insertPayload = {
      ogrenci_adi: addForm.name,
      veli_adi: addForm.parentName || null,
      telefon: addForm.phone || null,
      sehir: addForm.city || null,
      sinif: addForm.grade || null,
      branslar: addForm.branches.length ? addForm.branches : [],
      ders_tipi: lessonTypeToDb(addForm.lessonType),
      atanan_ogretmen_id: addForm.assignedTeacherId || null,
      aktif: addForm.status === 'Aktif',
      notlar: addForm.notes || null,
      ders_ucreti: addForm.dersUcreti !== '' ? Number(addForm.dersUcreti) : null,
    };
    const { error } = await supabase.from('egtpanel_ogrenciler').insert(insertPayload);
    setSaving(false);
    if (error) {
      console.error('Öğrenci ekleme hatası:', error);
      showToast(`Hata: ${error.message || 'Öğrenci eklenemedi.'}`);
      return;
    }
    showToast('Öğrenci başarıyla eklendi!');
    setShowAddModal(false);
    setAddForm({ name: '', parentName: '', phone: '', city: 'Antalya', grade: '', branches: [] as string[], lessonType: 'Birebir Online', assignedTeacherId: '', status: 'Aktif', notes: '', dersUcreti: '' });
    fetchData();
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editItem || !editForm) return;
    setSaving(true);
    const updatePayload = {
      ogrenci_adi: editForm.name,
      veli_adi: editForm.parentName || null,
      telefon: editForm.phone || null,
      sehir: editForm.city || null,
      sinif: editForm.grade || null,
      branslar: editForm.branches.length ? editForm.branches : [],
      ders_tipi: lessonTypeToDb(editForm.lessonType),
      atanan_ogretmen_id: editForm.assignedTeacherId || null,
      aktif: editForm.status === 'Aktif',
      notlar: editForm.notes || null,
      ders_ucreti: editForm.dersUcreti !== '' ? Number(editForm.dersUcreti) : null,
      guncelleme_tarihi: new Date().toISOString(),
    };
    const { error } = await supabase.from('egtpanel_ogrenciler').update(updatePayload).eq('id', editItem.id);
    setSaving(false);
    if (error) {
      console.error('Öğrenci güncelleme hatası:', error);
      showToast(`Hata: ${error.message || 'Güncellenemedi.'}`);
      return;
    }
    showToast('Öğrenci güncellendi!');
    setEditItem(null);
    setEditForm(null);
    fetchData();
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setSaving(true);
    const { error } = await supabase.from('egtpanel_ogrenciler').delete().eq('id', deleteItem.id);
    setSaving(false);
    if (error) { showToast('Hata: Silinemedi.'); return; }
    showToast('Öğrenci silindi.');
    setDeleteItem(null);
    fetchData();
  };

  const activeCount = data.filter((s) => s.status === 'Aktif').length;
  const otherCount = data.filter((s) => s.status !== 'Aktif').length;

  return (
    <div className="p-6 space-y-6">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-[#1e3a5f] text-white px-5 py-3 rounded-xl text-sm font-medium">
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Öğrenciler</h1>
          <p className="text-slate-500 text-sm mt-1">Tüm öğrencileri yönetin ve takip edin</p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 bg-[#1e3a5f] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#162d4a] transition-colors whitespace-nowrap cursor-pointer">
          <i className="ri-add-line" /> Yeni Öğrenci Ekle
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: 'Toplam Öğrenci', val: data.length, icon: 'ri-user-line', color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Aktif', val: activeCount, icon: 'ri-user-star-line', color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Takipte / Pasif', val: otherCount, icon: 'ri-user-follow-line', color: 'text-amber-700', bg: 'bg-amber-50' },
        ].map((s) => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4 flex items-center gap-4`}>
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white">
              <i className={`${s.icon} ${s.color}`} />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.val}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-100 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Öğrenci, veli veya telefon ara..." className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
          </div>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
            <option value="">Tüm Durumlar</option>
            <option>Aktif</option>
            <option>Pasif</option>
            <option>Takipte</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {['Öğrenci', 'Sınıf', 'Branşlar', 'Ders Tipi', 'Ders Ücreti', 'Öğretmen', 'Toplam Ders', 'Durum', 'İşlemler'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-16">
                    <i className="ri-user-search-line text-4xl text-slate-200 block mb-2" />
                    <p className="text-slate-400 text-sm">{data.length === 0 ? 'Henüz öğrenci eklenmemiş' : 'Öğrenci bulunamadı'}</p>
                  </td></tr>
                ) : filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="font-medium text-slate-800">{s.name}</div>
                      <div className="text-xs text-slate-400">{s.parentName} {s.phone && `· ${s.phone}`}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{s.grade || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {s.branches.length > 0 ? s.branches.map((b) => <span key={b} className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded text-xs">{b}</span>) : <span className="text-slate-400 text-xs">-</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">{s.lessonType || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {s.dersUcreti != null
                        ? <span className="font-semibold text-slate-800">&#8378;{s.dersUcreti.toLocaleString('tr-TR')}</span>
                        : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{s.assignedTeacherName}</td>
                    <td className="px-4 py-3 text-slate-700 font-medium whitespace-nowrap">{s.totalLessons}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[s.status]}`}>{s.status}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button onClick={() => navigate(`/admin/ogrenciler/${s.id}`)} className="text-[#1e3a5f] text-xs font-medium px-2.5 py-1.5 rounded-lg border border-[#1e3a5f]/20 hover:bg-[#1e3a5f]/5 transition-colors cursor-pointer whitespace-nowrap">
                          Detay
                        </button>
                        <button onClick={() => openEdit(s)} className="text-slate-600 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap">
                          <i className="ri-edit-line" />
                        </button>
                        <button onClick={() => setDeleteItem(s)} className="text-red-500 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-red-100 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap">
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editItem && editForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Öğrenciyi Düzenle</h2>
              <button onClick={() => { setEditItem(null); setEditForm(null); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Öğrenci Adı Soyadı *</label>
                  <input required value={editForm.name} onChange={(e) => setEditForm((p) => p ? ({ ...p, name: e.target.value }) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Veli Adı Soyadı</label>
                  <input value={editForm.parentName} onChange={(e) => setEditForm((p) => p ? ({ ...p, parentName: e.target.value }) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Telefon</label>
                  <input value={editForm.phone} onChange={(e) => setEditForm((p) => p ? ({ ...p, phone: e.target.value }) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Şehir</label>
                  <input value={editForm.city} onChange={(e) => setEditForm((p) => p ? ({ ...p, city: e.target.value }) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Sınıf</label>
                  <select value={editForm.grade} onChange={(e) => setEditForm((p) => p ? ({ ...p, grade: e.target.value }) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">Seçiniz</option>
                    {GRADE_LEVELS.map((g) => <option key={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ders Tipi</label>
                  <select value={editForm.lessonType} onChange={(e) => setEditForm((p) => p ? ({ ...p, lessonType: e.target.value }) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    {['Birebir Online', 'Yüz Yüze', 'Grup Dersi', 'KPSS'].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Atanan Öğretmen</label>
                  <select value={editForm.assignedTeacherId} onChange={(e) => setEditForm((p) => p ? ({ ...p, assignedTeacherId: e.target.value }) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">— Seçilmedi —</option>
                    {teachers.map((t) => <option key={t.id} value={t.id}>{t.ad_soyad}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Durum</label>
                  <select value={editForm.status} onChange={(e) => setEditForm((p) => p ? ({ ...p, status: e.target.value }) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option>Aktif</option>
                    <option>Pasif</option>
                    <option>Takipte</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Anlaşılan Ders Ücreti (₺ / ders)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">₺</span>
                    <input
                      type="number"
                      min="0"
                      placeholder="Örn: 375"
                      value={editForm.dersUcreti}
                      onChange={(e) => setEditForm((p) => p ? ({ ...p, dersUcreti: e.target.value }) : p)}
                      className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Branşlar</label>
                <div className="flex flex-wrap gap-2">
                  {BRANCHES.map((b) => (
                    <button key={b} type="button" onClick={() => toggleBranch(b)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${editForm.branches.includes(b) ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#1e3a5f]/40'}`}>
                      {b}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Notlar</label>
                <textarea value={editForm.notes} onChange={(e) => setEditForm((p) => p ? ({ ...p, notes: e.target.value }) : p)} rows={2} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setEditItem(null); setEditForm(null); }} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] disabled:opacity-60 cursor-pointer">
                  {saving ? 'Kaydediliyor...' : 'Güncelle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Yeni Öğrenci Ekle</h2>
              <button onClick={() => { setShowAddModal(false); setAddForm(emptyAddForm); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <form onSubmit={handleAddSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Öğrenci Adı Soyadı *</label>
                  <input required value={addForm.name} onChange={(e) => setAddForm((p) => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Veli Adı Soyadı</label>
                  <input value={addForm.parentName} onChange={(e) => setAddForm((p) => ({ ...p, parentName: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Telefon</label>
                  <input value={addForm.phone} onChange={(e) => setAddForm((p) => ({ ...p, phone: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Şehir</label>
                  <input value={addForm.city} onChange={(e) => setAddForm((p) => ({ ...p, city: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Sınıf</label>
                  <select value={addForm.grade} onChange={(e) => setAddForm((p) => ({ ...p, grade: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">Seçiniz</option>
                    {GRADE_LEVELS.map((g) => <option key={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ders Tipi</label>
                  <select value={addForm.lessonType} onChange={(e) => setAddForm((p) => ({ ...p, lessonType: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    {['Birebir Online', 'Yüz Yüze', 'Grup Dersi', 'KPSS'].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Atanan Öğretmen</label>
                  <select value={addForm.assignedTeacherId} onChange={(e) => setAddForm((p) => ({ ...p, assignedTeacherId: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">— Seçilmedi —</option>
                    {teachers.map((t) => <option key={t.id} value={t.id}>{t.ad_soyad}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Durum</label>
                  <select value={addForm.status} onChange={(e) => setAddForm((p) => ({ ...p, status: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option>Aktif</option>
                    <option>Pasif</option>
                    <option>Takipte</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Anlaşılan Ders Ücreti (₺ / ders)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">₺</span>
                    <input
                      type="number"
                      min="0"
                      placeholder="Örn: 375"
                      value={addForm.dersUcreti}
                      onChange={(e) => setAddForm((p) => ({ ...p, dersUcreti: e.target.value }))}
                      className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Branşlar</label>
                <div className="flex flex-wrap gap-2">
                  {BRANCHES.map((b) => (
                    <button key={b} type="button" onClick={() => toggleAddBranch(b)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                        addForm.branches.includes(b)
                          ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-[#1e3a5f]/40'
                      }`}>
                      {b}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Notlar</label>
                <textarea value={addForm.notes} onChange={(e) => setAddForm((p) => ({ ...p, notes: e.target.value }))} rows={2} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowAddModal(false); setAddForm(emptyAddForm); }} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] disabled:opacity-60 cursor-pointer">{saving ? 'Kaydediliyor...' : 'Öğrenci Ekle'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteItem && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl" />
            </div>
            <h3 className="font-bold text-slate-800 text-lg mb-1">Öğrenciyi Sil</h3>
            <p className="text-slate-500 text-sm mb-6">
              <strong>{deleteItem.name}</strong> adlı öğrenciyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteItem(null)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
              <button onClick={handleDelete} disabled={saving} className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 disabled:opacity-60 cursor-pointer">
                {saving ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}